/**
 * LocationsPage — manage physical locations (admin only).
 *
 * Locations are assigned to operators and devices to support
 * multi-site deployments.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Alert, CircularProgress, Tooltip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../api/client';

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const getLocations = () =>
  api.get('/api/v1/locations').then((r) => r.data);

const createLocation = (data) =>
  api.post('/api/v1/locations', data).then((r) => r.data);

const updateLocation = (id, data) =>
  api.put(`/api/v1/locations/${id}`, data).then((r) => r.data);

const deactivateLocation = (id) =>
  api.delete(`/api/v1/locations/${id}`).then((r) => r.data);

// ---------------------------------------------------------------------------
// LocationDialog (create / edit)
// ---------------------------------------------------------------------------

function LocationDialog({ open, onClose, location, onDone }) {
  const isEdit = !!location;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(location?.name || '');
      setDescription(location?.description || '');
      setAddress(location?.address || '');
      setError('');
    }
  }, [open, location]);

  const handle = async () => {
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        address: address.trim() || undefined,
      };
      if (isEdit) {
        await updateLocation(location.id, payload);
      } else {
        await createLocation(payload);
      }
      onDone(`Location ${isEdit ? 'updated' : 'created'} successfully.`);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} location.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Location' : 'New Location'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          required
          sx={{ mb: 2, mt: 1 }}
          autoFocus
        />
        <TextField
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Address (optional)"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          fullWidth
          size="small"
          multiline
          rows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handle} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// LocationsPage
// ---------------------------------------------------------------------------

export default function LocationsPage() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getLocations();
      setLocations(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load locations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDeactivate = async (loc) => {
    if (!window.confirm(`Deactivate location "${loc.name}"?`)) return;
    try {
      await deactivateLocation(loc.id);
      setToast(`Location "${loc.name}" deactivated.`);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Deactivation failed.');
    }
  };

  const handleActivate = async (loc) => {
    try {
      await updateLocation(loc.id, { is_active: true });
      setToast(`Location "${loc.name}" reactivated.`);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Reactivation failed.');
    }
  };

  const openCreate = () => { setEditTarget(null); setDialogOpen(true); };
  const openEdit = (loc) => { setEditTarget(loc); setDialogOpen(true); };

  const handleDone = (msg) => {
    setToast(msg);
    load();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Locations</Typography>
          <Typography variant="body2" color="text.secondary">
            Physical sites assigned to operators and devices
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Location
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && locations.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No locations yet. Create one to assign operators and devices to sites.
                  </TableCell>
                </TableRow>
              ) : locations.map((loc) => (
                <TableRow key={loc.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{loc.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {loc.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {loc.address || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={loc.is_active ? 'Active' : 'Inactive'}
                      color={loc.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontSize={12} color="text.secondary">
                      {loc.created_at ? new Date(loc.created_at).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(loc)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {loc.is_active ? (
                      <Tooltip title="Deactivate">
                        <IconButton size="small" color="warning" onClick={() => handleDeactivate(loc)}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Activate">
                        <IconButton size="small" color="success" onClick={() => handleActivate(loc)}>
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <LocationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        location={editTarget}
        onDone={handleDone}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast('')}
        message={toast}
      />
    </Box>
  );
}